import { Button, Center, Grid, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { StaticImage } from "gatsby-plugin-image";
import React, { ReactNode, useEffect, useRef, useState } from "react";

import { Card, ContactUsDrawer, Text, Title } from "@components";

interface LetsTalkProps {
  content?: ReactNode;
  showAwards?: boolean;
  title?: ReactNode;
}

export function LetsTalk(props: LetsTalkProps) {
  const {
    content = "Have an idea, project, or challenge you’d like to explore? We’d love to hear about it. Click below to get in touch and start the conversation.",
    showAwards = false,
    title = "Let’s talk.",
  } = props;
  const [contactUsOpened, { open: openContactUs, close: closeContactUs }] =
    useDisclosure(false);
  const hubspotFormRef = useRef<HTMLDivElement>(null);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    const scriptSrc = "https://js.hsforms.net/forms/embed/46087357.js";
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);

    if (!existingScript) {
      // First time loading the script
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.defer = true;
      document.body.appendChild(script);
    } else {
      // Script already loaded - force remount by changing key
      setFormKey((prev) => prev + 1);
    }
  }, []);

  return (
    <>
      <Stack gap="xs" hiddenFrom="md">
        <Card
          h="100%"
          p="xl"
          bg="brandPrimary"
          justify="center"
          backgroundImage={
            <StaticImage
              src="../../images/bg-primary-2.jpg"
              alt=""
              placeholder="blurred"
              width={1280}
              height={720}
              quality={80}
              style={{
                width: "100%",
                height: "100%",
              }}
              objectFit="cover"
              objectPosition="center"
            />
          }
        >
          <Title order={2} size="h4" c="fontLight" mb="md">
            {title}
          </Title>

          <Text size="sm" c="fontLight" mb="lg">
            {content}
          </Text>

          <Button fullWidth variant="glass-dark" onClick={openContactUs}>
            Contact Us
          </Button>
        </Card>

        {showAwards && (
          <Card
            p="xl"
            bg="layoutDark"
            style={{ overflow: "visible" }}
            backgroundImage={
              <StaticImage
                src="../../images/bg-dark-2.jpg"
                alt=""
                placeholder="blurred"
                width={1280}
                height={720}
                quality={80}
                style={{
                  width: "100%",
                  height: "100%",
                }}
                objectFit="cover"
                objectPosition="center"
              />
            }
          >
            <Center mt={-50} mb={-70}>
              <a href="#">
                <StaticImage
                  src="../../images/awards.png"
                  alt="Awards"
                  placeholder="blurred"
                  quality={80}
                  style={{
                    width: "240px",
                    height: "auto",
                  }}
                />
              </a>
            </Center>
          </Card>
        )}
      </Stack>

      <Grid gutter="xs" visibleFrom="md">
        <Grid.Col span={4}>
          <Card
            h="100%"
            p="xxl"
            py="xxxl"
            bg="brandPrimary"
            justify="center"
            backgroundImage={
              <StaticImage
                src="../../images/bg-primary-2.jpg"
                alt=""
                placeholder="blurred"
                width={1920}
                height={1080}
                quality={80}
                style={{
                  width: "100dvw",
                }}
                objectPosition="left top"
              />
            }
          >
            <Title order={2} c="fontLight" mb="md">
              {title}
            </Title>

            <Text c="fontLight">{content}</Text>
          </Card>
        </Grid.Col>

        <Grid.Col span={8}>
          <Card
            h="100%"
            p="xxl"
            py="xxxl"
            bg="layoutLight"
            justify="center"
            backgroundImage={
              <StaticImage
                src="../../images/bg-light-2.jpg"
                alt=""
                placeholder="blurred"
                width={1920}
                height={1080}
                quality={80}
                style={{
                  width: "100dvw",
                }}
                objectPosition="-33dvw top"
              />
            }
          >
            <div
              key={formKey}
              ref={hubspotFormRef}
              className="hs-form-frame"
              data-region="na1"
              data-form-id="baeb5cbc-5085-4c30-9d68-94d6c6466b31"
              data-portal-id="46087357"
              style={{
                margin: -40,
              }}
            />
          </Card>
        </Grid.Col>

        {showAwards && (
          <Grid.Col span={12}>
            <Card
              p="xxl"
              py="xxxl"
              bg="layoutDark"
              style={{ overflow: "visible" }}
              backgroundImage={
                <StaticImage
                  src="../../images/bg-dark-2.jpg"
                  alt=""
                  placeholder="blurred"
                  width={1920}
                  height={1080}
                  quality={80}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                  objectFit="cover"
                  objectPosition="center bottom"
                />
              }
              hiddenFrom="lg"
            >
              <Grid gutter="xxl">
                <Grid.Col span={6}>
                  <Title order={2} ta="right" c="fontLight">
                    Industry-recognized.
                    <br />
                    Client-trusted.
                  </Title>
                </Grid.Col>

                <Grid.Col
                  span={6}
                  pos="relative"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    overflow: "visible",
                  }}
                >
                  <a
                    href="#"
                    style={{ position: "absolute", top: "-50%", zIndex: 1 }}
                  >
                    <StaticImage
                      src="../../images/awards.png"
                      alt="Awards"
                      placeholder="blurred"
                      quality={80}
                      style={{
                        width: "378px",
                        height: "auto",
                      }}
                    />
                  </a>
                </Grid.Col>
              </Grid>
            </Card>

            <Card
              p="xxl"
              py="xxxl"
              bg="layoutDark"
              style={{ overflow: "visible" }}
              backgroundImage={
                <StaticImage
                  src="../../images/bg-dark-2.jpg"
                  alt=""
                  placeholder="blurred"
                  width={1920}
                  height={1080}
                  quality={80}
                  style={{
                    width: "100dvw",
                  }}
                  objectPosition="left -420px"
                />
              }
              visibleFrom="lg"
            >
              <Grid gutter="xxl">
                <Grid.Col span={6}>
                  <Title order={2} ta="right" c="fontLight">
                    Industry-recognized.
                    <br />
                    Client-trusted.
                  </Title>
                </Grid.Col>

                <Grid.Col
                  span={6}
                  pos="relative"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    overflow: "visible",
                  }}
                >
                  <a
                    href="#"
                    style={{ position: "absolute", top: "-50%", zIndex: 1 }}
                  >
                    <StaticImage
                      src="../../images/awards.png"
                      alt="Awards"
                      placeholder="blurred"
                      quality={80}
                      style={{
                        width: "378px",
                        height: "auto",
                      }}
                    />
                  </a>
                </Grid.Col>
              </Grid>
            </Card>
          </Grid.Col>
        )}
      </Grid>

      <ContactUsDrawer
        opened={contactUsOpened}
        onClose={closeContactUs}
        onCloseStack={closeContactUs}
      />
    </>
  );
}
